import React, { useEffect, useState } from "react";

import DatePicker, { ReactDatePickerProps } from "react-datepicker";
import { useFormikContext, useField } from "formik";
import getUserLocale from "get-user-locale";
import { TooltipIconData } from "@avamae/formbuilder/dist/FormBuilder";
import { FormFieldTooltip } from "components/helpers/FormFieldTooltip/FormFieldTooltip";
import { ErrorM } from "components/helpers/FormHelpers/ErrorM";
import { FormField } from "@avamae/formbuilder";
import { formatDate } from "components/helpers/filterBuilder/FilterBuilder";
import { convertDateToUTC } from "helpers/convertDateToUTC";
import { format, parseISO } from "date-fns";
import { parseListDate } from "helpers/parseListDate";
import { stringToDate } from "helpers/stringToDate";
import editIcon from "assets/edit_icon.png";
export interface FormDateTimeFieldProps<T> {
  fieldName: keyof T;
  label: string;
  readOnly?: boolean;
  onChange?: (
    date: Date | null,
    event: React.SyntheticEvent<any> | undefined
  ) => void;
  datePickerProps?: Omit<ReactDatePickerProps, "onChange">;
  tooltip?: string | null;
  tooltipIconData?: TooltipIconData | null;
  editReadOnly?: boolean;
  showEditIcon?: boolean;
  addBottomMarginWhenMenuIsOpen?: boolean;
  showClearIcon?: boolean;
}

export const parseExistingDate = (dateString: string) => {
  try {
    const splitDateTime = dateString.split("T");

    if (splitDateTime.length === 2) {
      const parsedDate = stringToDate(dateString);

      const parsedTime = splitDateTime[1].split(":");
      if (parsedTime.length === 3) {
        parsedDate.setHours(
          parseInt(parsedTime[0]),
          parseInt(parsedTime[1]),
          parseInt(parsedTime[2])
        );
      }

      return parsedDate;
    } else {
      return stringToDate(dateString);
    }
  } catch {
    console.log("Failed to parse list date: ", dateString);

    return stringToDate(dateString);
  }
};

const DateTimeFieldV2 = function<T>({
  fieldName,
  label,
  readOnly,
  datePickerProps,
  onChange,
  tooltipIconData,
  tooltip,
  editReadOnly,
  showEditIcon,
  addBottomMarginWhenMenuIsOpen,
  showClearIcon,
}: FormDateTimeFieldProps<T>) {
  const [field] = useField(fieldName as string);
  const formik = useFormikContext();
  const [readOnlyDate, setReadOnlyDate] = useState<string | null>(null);
  const [dateValue, setDateValue] = useState<Date | null>(null);
  const [relevantFormikError, setRelevantFormikError] = useState<string | null>(
    null
  );
  const [calendarOpen, setCalendarOpen] = useState<boolean>(false);
  const [focus, setFocus] = useState<boolean>(false);

  const formikErrors = formik.errors;

  useEffect(() => {
    if (formikErrors.hasOwnProperty(field.name)) {
      const error = formikErrors[field.name as keyof typeof formikErrors];

      if (typeof error === "string") {
        setRelevantFormikError(error);
      }
    } else {
      setRelevantFormikError(null);
    }
  }, [formikErrors]);

  const handleFocus = () => {
    setFocus(!focus);
  };

  const handleChange = (date: Date | null) => {
    let datepickerDate = date;

    let parsedDate: Date | null = null;

    let parsedDate2: string | null = null;

    const offset = date ? date.getTimezoneOffset() * 60000 : null;

    if (date && offset !== null) {
      const tempDate = new Date(date.setHours(0, 0, 0));

      datepickerDate = tempDate;
      parsedDate = new Date(tempDate.getTime() - offset);
    }

    if (parsedDate) {
      const year = parsedDate.getUTCFullYear();
      const month = parsedDate.getUTCMonth() + 1;
      const day = parsedDate.getUTCDate();

      const yearString = `${year}`;
      let monthString = `${month}`;
      let dayString = `${day}`;

      if (month < 10) {
        monthString = `0${month}`;
      }

      if (day < 10) {
        dayString = `0${day}`;
      }

      parsedDate2 = `${yearString}-${monthString}-${dayString}T00:00:00`;
    }

    if (datepickerDate) {
      setDateValue(datepickerDate);
    }
    else {
      setDateValue(null);
    }

    if (parsedDate2) {
      formik.setFieldValue(fieldName as string, parsedDate2);
    }
    else {
      formik.setFieldValue(fieldName as string, null);
    }
  };

  useEffect(() => {
    const selectedDate =
      field.value && typeof field.value === "string"
        ? parseExistingDate(field.value)
        : null;

    setDateValue(selectedDate);

    if (field.value) {
      setReadOnlyDate(parseListDate(field.value, true));
    }
  }, []);
  const locale = getUserLocale();

  const clearValue = () => {
    setDateValue(null);
    formik.setFieldValue(fieldName as string, null);
    handleChange(null);
  };

  return (
    <div
      className={`FormBox FormSideBySide DateTimeFieldV2 ${
        addBottomMarginWhenMenuIsOpen && calendarOpen ? "AddBottomMargin" : ""
      } ${editReadOnly ? "DateTimeEditReadOnly" : ""}`}
    >
      <div className="FormLabel">
        <label>{label}</label>
        {showEditIcon && (
          <img src={editIcon} alt="edit icon" className="EditIcon" />
        )}

        {tooltip &&
          tooltipIconData &&
          tooltipIconData.mainIcon &&
          tooltipIconData.closeIcon && (
            <FormFieldTooltip
              tooltipText={tooltip}
              tooltipMainIconSource={tooltipIconData.mainIcon}
              tooltipCloseIconSource={tooltipIconData.closeIcon}
            />
          )}
      </div>
      <div
        className={`FormField ${
          formik.submitCount > 0 && relevantFormikError ? "Error" : ""
        } 
        `}
        onFocus={handleFocus}
        onBlur={handleFocus}
      >
        {readOnly ? (
          <div className="EditBox ViewOnly">
            {readOnlyDate ? `${readOnlyDate}` : "No date"}
          </div>
        ) : (
          <>
            <DatePicker
              dateFormat="dd-MMM-yyyy"
              locale={locale}
              className={readOnly ? "EditBox ViewOnly" : "EditBox"}
              onChange={onChange ? onChange : handleChange}
              onCalendarOpen={() => setCalendarOpen(true)}
              onCalendarClose={() => setCalendarOpen(false)}
              selected={dateValue}
              readOnly={readOnly}
              placeholderText={readOnly ? "" : "Select date..."}
              {...datePickerProps}
            />
            {!readOnly && dateValue && showClearIcon && (
            <svg
              className={`clearDate ${focus ? "Datefocus" : ""}`}
              onClick={clearValue}
            >
              <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"></path>
            </svg>
          )}
            <ErrorM name={fieldName.toString()} />
          </>
        )}
      </div>
    </div>
  );
};

export { DateTimeFieldV2 };

export const dateTimeV2Renderer = (
  readOnly: boolean,
  editReadOnly?: boolean,
  showEditIcon?: boolean,
  addBottomMarginWhenMenuIsOpen?: boolean,
  showClearIcon?: boolean
) =>
  function dateTimeV2Renderer<T>(_key: React.ReactText, field: FormField<T>) {
    return (
      <DateTimeFieldV2
        label={field.label as string}
        readOnly={readOnly}
        fieldName={field.name as string}
        editReadOnly={editReadOnly}
        showEditIcon={showEditIcon}
        addBottomMarginWhenMenuIsOpen={addBottomMarginWhenMenuIsOpen}
        showClearIcon={showClearIcon}
      />
    );
  };
